/** A coarse document family used to decide how the attachment renders (image → thumbnail/inline,
 * text → raw text, everything else → DocViewer) and its human-readable format label. The card/panel
 * icon itself comes from `getFileIcon` (@commenda/utils), the app's canonical file-type glyph set. */
export type AttachmentKind =
  | 'image'
  | 'pdf'
  | 'word'
  | 'spreadsheet'
  | 'text'
  | 'file';

export interface AttachmentKindInfo {
  kind: AttachmentKind;
  /** Format label shown under the filename, e.g. "PDF", "Word document". */
  label: string;
}

/** Display name for an attachment: drop the extension, turn separators into spaces, and Title Case
 * each word (first letter up, rest preserved so acronyms like "PM"/"US" survive). The real filename
 * is still used for the download so the saved file keeps its extension. */
export function formatAttachmentName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const spaced = base.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase());
  return titled || fileName;
}

const EXT_RE = /\.([a-z0-9]+)(?:\?|#|$)/i;

function extensionOf(fileName: string, url?: string): string {
  return (
    fileName.match(EXT_RE)?.[1] ??
    url?.match(EXT_RE)?.[1] ??
    ''
  ).toLowerCase();
}

const KIND_ICON_EXT: Record<AttachmentKind, string> = {
  image: 'png',
  pdf: 'pdf',
  word: 'docx',
  spreadsheet: 'xlsx',
  text: 'txt',
  file: '',
};

export function getAttachmentIconExt(
  kind: AttachmentKind,
  fileName: string,
  url?: string
): string {
  return extensionOf(fileName, url) || KIND_ICON_EXT[kind];
}

const IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'ico',
  'svg',
]);

/** Resolve a file's kind/label from its mime type, falling back to its extension (mime is often a
 * generic "application/octet-stream" for uploads, so the extension is the reliable signal). */
export function getAttachmentKind(
  mimeType: string,
  fileName: string,
  url?: string
): AttachmentKindInfo {
  const mime = (mimeType || '').toLowerCase();
  const ext = extensionOf(fileName, url);

  if (mime.startsWith('image/') || IMAGE_EXTS.has(ext)) {
    return { kind: 'image', label: 'Image' };
  }
  if (mime === 'application/pdf' || ext === 'pdf') {
    return { kind: 'pdf', label: 'PDF' };
  }
  if (
    mime.includes('wordprocessingml') ||
    mime.includes('msword') ||
    ext === 'doc' ||
    ext === 'docx'
  ) {
    return { kind: 'word', label: 'Word document' };
  }
  if (
    mime === 'text/csv' ||
    mime.includes('spreadsheetml') ||
    mime.includes('ms-excel') ||
    ext === 'csv' ||
    ext === 'xls' ||
    ext === 'xlsx'
  ) {
    return {
      kind: 'spreadsheet',
      label: ext === 'csv' || mime === 'text/csv' ? 'CSV' : 'Spreadsheet',
    };
  }
  if (
    mime.startsWith('text/') ||
    ext === 'txt' ||
    ext === 'md' ||
    ext === 'markdown'
  ) {
    return {
      kind: 'text',
      label: ext === 'md' || ext === 'markdown' ? 'Markdown' : 'Text',
    };
  }
  return { kind: 'file', label: 'File' };
}
